import { doNotTranslate } from 'util/misc'

export const PERFORMANCE_CALCULATORS_AUTO = 0
export const PERFORMANCE_CALCULATORS_PVWATTS_V6 = 1
export const PERFORMANCE_CALCULATORS_SAM = 2
export const PERFORMANCE_CALCULATORS_MCS = 3
export const PERFORMANCE_CALCULATORS_PVWATTS_V8 = 4
export const PERFORMANCE_CALCULATORS_3RD_PARTY = 5
export const PERFORMANCE_CALCULATORS_MCS_AND_SAM = 6
export const PERFORMANCE_CALCULATORS_SAM_2025 = 7
export const PERFORMANCE_CALCULATORS_SAM_2025_R1 = 9

// NOTE: Keep in sync with django/oslib/performance_calculators/mcs_core/constants.py MCS_ALLOWED_COUNTRIES
export const MCS_ALLOWED_COUNTRIES: string[] = ['GB', 'IM']

// NOTE: Keep in sync with django/oslib/utils.py McsCalculatorValues
export const MCS_MAX_ARRAY_SIZE_KW = 50
export const MCS_MAX_ANNUAL_USAGE = 5999
export const MCS_MAX_ANNUAL_SOLAR_YIELD = 5999
export const MCS_MAX_BATTERY_USABLE_CAPACITY_KWH = 15.1

export type PERFORMANCE_CALCULATOR_ID =
  | typeof PERFORMANCE_CALCULATORS_AUTO
  | typeof PERFORMANCE_CALCULATORS_PVWATTS_V6
  | typeof PERFORMANCE_CALCULATORS_SAM
  | typeof PERFORMANCE_CALCULATORS_SAM_2025
  | typeof PERFORMANCE_CALCULATORS_MCS
  | typeof PERFORMANCE_CALCULATORS_PVWATTS_V8
  | typeof PERFORMANCE_CALCULATORS_3RD_PARTY
  | typeof PERFORMANCE_CALCULATORS_MCS_AND_SAM
  | typeof PERFORMANCE_CALCULATORS_SAM_2025_R1

export const SELF_CONSUMPTION_CALCULATORS_DYNAMIC = 1
export const SELF_CONSUMPTION_CALCULATORS_OPENSOLAR = 2

export type SELF_CONSUMPTION_CALCULATOR_ID =
  | typeof SELF_CONSUMPTION_CALCULATORS_DYNAMIC
  | typeof SELF_CONSUMPTION_CALCULATORS_OPENSOLAR

export const PERFORMANCE_CALCULATORS = [
  {
    id: PERFORMANCE_CALCULATORS_AUTO,
    name: 'Auto',
    translate: true,
    configuration_level_only: true,
  },
  {
    id: PERFORMANCE_CALCULATORS_SAM,
    name: 'System Advisor Model 2020.02.29.r2',
    translate: false,
    supports_shading: true,
    supports_automated_shading: true,
  },
  {
    id: PERFORMANCE_CALCULATORS_SAM_2025,
    name: 'System Advisor Model 2025.4.16 (Sunset)',
    translate: false,
    supports_shading: true,
    supports_automated_shading: true,
    hide_from_choices: true,
  },
  {
    id: PERFORMANCE_CALCULATORS_MCS,
    name: 'MCS (Fixed)',
    translate: false,
    restrict_to_country: MCS_ALLOWED_COUNTRIES,
    supports_automated_shading: true,
    hide_from_choices: false,
  },
  {
    id: PERFORMANCE_CALCULATORS_PVWATTS_V8,
    name: 'PVWatts v8',
    translate: false,
    supports_automated_shading: false,
  },
  // PVWatts v6 has been decommissioned in early 2024 and has been replaced by PVWatts v8: https://developer.nlr.gov/docs/solar/pvwatts/v8/
  {
    id: PERFORMANCE_CALCULATORS_PVWATTS_V6,
    name: 'PVWatts v6 (Deprecated)',
    translate: false,
    supports_automated_shading: false,
    hide_from_choices: true,
  },
  {
    id: PERFORMANCE_CALCULATORS_3RD_PARTY,
    name: 'Import 3rd Party',
    translate: true,
    supports_automated_shading: false,
    hide_from_choices: false,
    system_level_only: true,
  },
  {
    id: PERFORMANCE_CALCULATORS_MCS_AND_SAM,
    name: 'MCS + SAM (Auto)',
    translate: false,
    restrict_to_country: MCS_ALLOWED_COUNTRIES,
    supports_automated_shading: true,
    hide_from_choices: false,
  },
  {
    id: PERFORMANCE_CALCULATORS_SAM_2025_R1,
    name: 'System Advisor Model 2025.4.16 Revision 1',
    translate: false,
    supports_automated_shading: true,
    hide_from_choices: false,
  },
]

export const SELF_CONSUMPTION_CALCULATORS = [
  {
    id: SELF_CONSUMPTION_CALCULATORS_DYNAMIC,
    name: 'Auto (default)',
  },
  {
    id: SELF_CONSUMPTION_CALCULATORS_OPENSOLAR,
    name: 'OpenSolar (Override)',
  },
]

export function supportsAnyShading(calculatorId: PERFORMANCE_CALCULATOR_ID): boolean {
  return calculatorId === 1 || calculatorId === 3 || calculatorId === 4 || calculatorId === 6
}

export function isSamCalculator(calculatorId: number): boolean {
  return (
    calculatorId === PERFORMANCE_CALCULATORS_MCS_AND_SAM ||
    calculatorId === PERFORMANCE_CALCULATORS_SAM ||
    calculatorId === PERFORMANCE_CALCULATORS_SAM_2025 ||
    calculatorId === PERFORMANCE_CALCULATORS_SAM_2025_R1
  )
}

export function isSam2025Calculator(calculatorId: number): boolean {
  return calculatorId === PERFORMANCE_CALCULATORS_SAM_2025 || calculatorId === PERFORMANCE_CALCULATORS_SAM_2025_R1
}

export function isSam2020Calculator(calculatorId: number): boolean {
  return calculatorId === PERFORMANCE_CALCULATORS_SAM
}

export const getPerformanceCalculatorChoices = (
  selectedCalculator: number,
  country_iso2: string,
  filteredCalculators: number[] = [],
  isProjectConfiguration: boolean = false
) => {
  return PERFORMANCE_CALCULATORS.filter((calculator) => {
    if (!isProjectConfiguration && calculator?.configuration_level_only === true) {
      return false
    }
    if (isProjectConfiguration && calculator?.system_level_only === true) {
      return false
    }
    if (selectedCalculator === calculator.id) {
      return true
    }
    if (filteredCalculators.includes(calculator.id)) {
      return false
    }
    if (calculator?.hide_from_choices === true) {
      return false
    } else if (calculator.restrict_to_country) {
      return calculator.restrict_to_country.includes(country_iso2)
    } else {
      return true
    }
  })
}

export const getMCSAndSAMSelfConsumptionCalculatorChoices = () => {
  return SELF_CONSUMPTION_CALCULATORS
}

export const translateCalculators = (calculators, translate: (term: string) => string) => {
  return calculators.map((pc) => ({
    ...pc,
    name: pc.translate ? translate(pc.name) : doNotTranslate(pc.name),
  }))
}

/**
 * Checks if the system is within MCS Self Consumption Boundaries based on specific thresholds.
 *
 * @param {SystemProps}
 * @returns {boolean} - Returns true if within MCS Self Consumption Boundaries, else false.
 */
export function withinMCSGuidelines(system) {
  return (
    system.userData?.kwStc <= MCS_MAX_ARRAY_SIZE_KW &&
    system.consumption?.usage_annual_proposed <= MCS_MAX_ANNUAL_USAGE &&
    system.output?.annual <= MCS_MAX_ANNUAL_SOLAR_YIELD &&
    (system.batteryTotalUsableKwh?.() ?? 0) < MCS_MAX_BATTERY_USABLE_CAPACITY_KWH
  )
}
